import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Chip, Card, CardContent, useTheme, useMediaQuery, Stack, Divider } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarColumnsButton, GridToolbarDensitySelector } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { YouTrackIssueDTO } from '../../apiClient/Api';
import { useApplicationContext } from 'ApplicationContext';

/**
 * MyTickets component displays YouTrack tickets for the current user.
 */
const MyTickets: React.FC = () => {
  const { t } = useTranslation();
  const context = useApplicationContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState<YouTrackIssueDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get filterEmail from URL query parameters
  const filterEmail = searchParams.get('filterEmail');

  useEffect(() => {
    context.setTitle(t('myTickets.title'));
    fetchTickets();
  }, [filterEmail]);

  /**
   * Fetches tickets from YouTrack API.
   */
  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await context.api.youTrack.youTrackGetTickets({
        top: 100,
        skip: 0,
        filterEmail: filterEmail || undefined
      });
      setTickets(response.data);
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
      context.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gets the color for status chip based on status text.
   */
  const getStatusColor = (status: string | null | undefined): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    if (!status) return 'default';
    
    const statusLower = status.toLowerCase();
    // New/Neu = red
    if (statusLower.includes('new') || statusLower.includes('neu')) return 'error';
    // Solved/Erledigt/Done = green
    if (statusLower.includes('solved') || statusLower.includes('erledigt') || statusLower.includes('done') || statusLower.includes('closed')) return 'success';
    // Everything else = orange
    return 'warning';
  };

  /**
   * Gets translated status text with fallback to original.
   */
  const getStatusTranslation = (status: string | null | undefined): string => {
    if (!status) return '-';
    
    const statusLower = status.toLowerCase();
    if (statusLower === 'new') return t('myTickets.statusNew');
    if (statusLower === 'solved') return t('myTickets.statusSolved');
    if (statusLower === 'open' || statusLower === 'offen') return t('myTickets.statusOpen');
    if (statusLower.includes('progress') || statusLower.includes('bearbeitung')) return t('myTickets.statusInProgress');
    
    // Fallback to original status
    return status;
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </GridToolbarContainer>
    );
  };

  /**
   * Formats timestamp to readable date string.
   */
  const formatDate = (timestamp: number | null | undefined): string => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  /**
   * Formats timestamp to short date for mobile.
   */
  const formatDateShort = (timestamp: number | null | undefined): string => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString();
  };

  /**
   * Renders mobile card view for tickets.
   */
  const renderMobileView = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography>{t('myTickets.loading') || 'Loading...'}</Typography>
        </Box>
      );
    }

    if (tickets.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('myTickets.noTickets') || 'No tickets found'}</Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        {tickets.map((ticket) => (
          <Card
            key={ticket.idReadable}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 3,
              },
            }}
            onClick={() => {
              const path = `/tickets/${ticket.idReadable}`;
              navigate(filterEmail ? `${path}?filterEmail=${encodeURIComponent(filterEmail)}` : path);
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Chip
                  label={getStatusTranslation(ticket.status)}
                  size="small"
                  color={getStatusColor(ticket.status)}
                />
                <Typography
                  variant="caption"
                  sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'primary.main', lineHeight: 'inherit' }}
                >
                  {ticket.idReadable}
                </Typography>
              </Box>
              
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                {ticket.summary}
              </Typography>
              
              <Divider sx={{ my: 1 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">
                  {t('myTickets.created')}: {formatDateShort(ticket.created)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('myTickets.updated')}: {formatDateShort(ticket.updated)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: t('myTickets.status'),
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Chip 
            label={getStatusTranslation(params.value as string)} 
            size="small"
            color={getStatusColor(params.value as string)}
          />
        ) : null
      )
    },
    {
      field: 'idReadable',
      headerName: t('myTickets.ticketId'),
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 'bold', lineHeight: 'inherit' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'summary',
      headerName: t('myTickets.summary'),
      width: 300,
      flex: 1
    },
    {
      field: 'created',
      headerName: t('myTickets.created'),
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatDate(params.value as number)}
        </Typography>
      )
    },
    {
      field: 'updated',
      headerName: t('myTickets.updated'),
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatDate(params.value as number)}
        </Typography>
      )
    }
  ];

  return (
    <Container maxWidth={false}>
      <Typography variant="h4" gutterBottom>
        {t('myTickets.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        {t('myTickets.description')}
      </Typography>
      
      {filterEmail && (
        <Box sx={{ mb: 2 }}>
          <Chip 
            label={`${t('myTickets.filteredByEmail')}: ${filterEmail}`}
            color="primary"
            variant="outlined"
          />
        </Box>
      )}
      
      {isMobile ? (
        renderMobileView()
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={tickets}
            columns={columns}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            getRowId={(row) => row.idReadable || Math.random().toString()}
            onRowClick={(params) => {
              const path = `/tickets/${params.row.idReadable}`;
              navigate(filterEmail ? `${path}?filterEmail=${encodeURIComponent(filterEmail)}` : path);
            }}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer'
              }
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 }
              },
              sorting: {
                sortModel: [{ field: 'updated', sort: 'desc' }]
              }
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}
    </Container>
  );
};

export default MyTickets;
